import useRequest from "@/lib/request/useRequest";
import {
    Button,
    Card,
    CardBody,
    Input,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    Spinner,
} from "@heroui/react";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useRef } from "react";
import useAuth from "@/lib/auth/useAuth";
import { getMapboxToken } from "@/lib/utils/map";
import { Organization } from "@/types/organization";
import useConfirm from "@/lib/confirm/useConfirm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faPhone, faInfo } from "@fortawesome/free-solid-svg-icons";
import sentinelLogo from "@/assets/images/Sentinel-Logo-Icon.png";

type CspPreference = "preferred" | "default";

export type NearbyCsp = {
    organization: {
        id: number;
        name: string;
        logo_url: string | null;
        csp_preference: CspPreference | null;
        account_manager_email: string | null;
        account_manager_name: string | null;
    };
    contact_name: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    city: string | null;
    state: string | null;
    distance_miles: number | null;
    location_lat: number | null;
    location_lon: number | null;
};

function CspCard({
    csp,
    isElite,
    isSelected,
    onSelect,
    onClick,
    isLoading,
}: {
    csp: NearbyCsp;
    isElite?: boolean;
    isSelected?: boolean;
    onSelect: () => void;
    onClick?: () => void;
    isLoading: boolean;
}) {
    const [showInfo, setShowInfo] = useState(false);

    const hasContactInfo = csp.contact_email || csp.contact_phone;

    const locationText =
        [csp.city, csp.state].filter(Boolean).join(", ") || "—";

    const distanceText =
        typeof csp.distance_miles === "number"
            ? `${csp.distance_miles.toFixed(1)} mi`
            : null;

    return (
        <>
            <Card
                className={[
                    isElite
                        ? "border-2 border-brandNavy-500/30 bg-brandNavy-500/5"
                        : undefined,
                    isSelected
                        ? "ring-2 ring-primary-500 bg-primary-500/5"
                        : undefined,
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                <CardBody className="flex flex-row items-center justify-between gap-3">
                    <div
                        className={`flex items-center gap-3 flex-1 ${onClick ? "cursor-pointer" : ""}`}
                        onClick={onClick}
                    >
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <div className="font-medium text-base">
                                    {csp.organization.name}
                                </div>  
                                {isElite ? (
                                    <span className="inline-flex items-center rounded-full bg-brandNavy-500/10 text-brandNavy-500 text-[11px] font-semibold px-2 py-0.5">
                                        ★ Elite
                                    </span>
                                ) : null}
                            </div>
                            <div className="text-sm text-foreground-500">
                                {locationText}
                                {distanceText ? ` • ${distanceText}` : ""}
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-row gap-2 items-center">
                        {csp.organization.logo_url ? (
                            <img
                                src={csp.organization.logo_url}
                                alt={`${csp.organization.name} logo`}
                                className="w-40 h-auto max-h-16 object-contain"
                            />
                        ) : null}

                        <div className="flex flex-col gap-1 items-stretch">
                            <Button
                                color="secondary"
                                onPress={onSelect}
                                isLoading={isLoading}
                                isDisabled={isLoading}
                                className="text-sm"
                                size="sm"
                            >
                                Select
                            </Button>
                            {hasContactInfo ? (
                                <Button
                                    variant="solid"
                                    color="primary"
                                    size="sm"
                                    className="text-sm"
                                    onPress={() => setShowInfo(true)}
                                    aria-label={`Show contact info for ${csp.organization.name}`}
                                >
                                    Contact Info
                                </Button>
                            ) : null}
                        </div>
                    </div>
                </CardBody>
            </Card>

            <Modal
                isOpen={showInfo}
                onClose={() => setShowInfo(false)}
                size="sm"
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col items-center gap-2 pt-6 pb-2">
                        {csp.organization.logo_url ? (
                            <img
                                src={csp.organization.logo_url}
                                alt={`${csp.organization.name} logo`}
                                className="w-40 h-auto max-h-16 object-contain"
                            />
                        ) : null}
                        <div className="text-center">
                            <div className="text-lg">
                                {csp.organization.name}
                            </div>
                            <div className="text-xs font-normal text-foreground-500">
                                {locationText}
                                {distanceText ? ` • ${distanceText}` : ""}
                            </div>
                        </div>
                    </ModalHeader>
                    <ModalBody className="pb-6">
                        <div className="flex flex-col gap-3">
                            {csp.contact_name ? (
                                <div className="text-lg font-semibold text-foreground-800 text-center">
                                    {csp.contact_name}
                                </div>
                            ) : null}
                            {csp.contact_phone ? (
                                <div className="flex items-center gap-3 p-3 rounded-lg bg-default-100">
                                    <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm shrink-0">
                                        <FontAwesomeIcon icon={faPhone} />
                                    </div>
                                    <div>
                                        <div className="text-xs text-foreground-500">
                                            Phone
                                        </div>
                                        <a
                                            href={`tel:${csp.contact_phone}`}
                                            className="text-base text-primary-500 font-medium"
                                        >
                                            {csp.contact_phone}
                                        </a>
                                    </div>
                                </div>
                            ) : null}
                            {csp.contact_email ? (
                                <div className="flex items-center gap-3 p-3 rounded-lg bg-default-100">
                                    <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm shrink-0">
                                        <FontAwesomeIcon icon={faEnvelope} />
                                    </div>
                                    <div>
                                        <div className="text-xs text-foreground-500">
                                            Email
                                        </div>
                                        <a
                                            href={`mailto:${csp.contact_email}`}
                                            className="break-all text-sm text-primary-500 font-medium"
                                        >
                                            {csp.contact_email}
                                        </a>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

function SentinelContactCard({ csps }: { csps: NearbyCsp[] }) {
    const [showInfo, setShowInfo] = useState(false);

    const contact = useMemo(() => {
        const nearest = csps.find((c) => c.organization.account_manager_email);
        return {
            name:
                nearest?.organization.account_manager_name ??
                "Sentinel Support",
            email:
                nearest?.organization.account_manager_email ??
                "colten.salm@sentinelfertigation.com",
        };
    }, [csps]);

    

    return (
        <>
            <Card className="border-2 border-green-700/30 bg-green-700/5">
                <CardBody className="flex flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-3 flex-1">
                        <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <div className="font-medium">
                                    Sentinel Support
                                </div>
                                <span className="inline-flex items-center rounded-full bg-green-700/10 text-green-700 text-[11px] font-semibold px-2 py-0.5">
                                    ★ Support
                                </span>
                            </div>
                            <div className="text-xs text-foreground-500">
                                Can't find what you need?
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-row gap-2 items-center">
                        <Button
                            isIconOnly
                            variant="solid"
                            color="primary"
                            size="sm"
                            radius="full"
                            onPress={() => setShowInfo(true)}
                            aria-label="Show Sentinel contact info"
                        >
                            <FontAwesomeIcon
                                icon={faInfo}
                                className="text-md"
                            />
                        </Button>
                        <Button
                            color="success"
                            className="text-white"
                            onPress={() =>
                                window.open("https://sentinelag.tech/contact", "_blank", "noopener,noreferrer")
                            }
                        >
                            Get Support
                        </Button>
                    </div>
                </CardBody>
            </Card>

            <Modal
                isOpen={showInfo}
                onClose={() => setShowInfo(false)}
                size="sm"
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col items-center gap-2 pt-6 pb-2">
                        <img
                            src={sentinelLogo}
                            alt="Sentinel logo"
                            className="w-40 h-auto max-h-16 object-contain"
                        />
                        <div className="text-center">
                            <div className="text-lg">Sentinel Support</div>
                            <div className="text-sm font-normal text-foreground-700">
                                {contact.name}
                            </div>
                        </div>
                    </ModalHeader>
                    <ModalBody className="pb-6">
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-default-100">
                                <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-sm shrink-0">
                                    <FontAwesomeIcon icon={faEnvelope} />
                                </div>
                                <div>
                                    <div className="text-xs text-foreground-500">
                                        Email
                                    </div>
                                    <a
                                        href={`mailto:${contact.email}`}
                                        className="break-all text-sm text-primary-500 font-medium"
                                    >
                                        {contact.email}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

export default function CspLocatorPanel({
    selectedCspId,
    onCspClick,
    onSearchResult,
    cardRefs,
}: {
    selectedCspId?: number | null;
    onCspClick?: (csp: NearbyCsp) => void;
    onSearchResult?: (data: {
        coords: { lat: number; lon: number } | null;
        eliteCsps: NearbyCsp[];
        defaultCsps: NearbyCsp[];
    }) => void;
    cardRefs?: React.MutableRefObject<Map<number, HTMLDivElement>>;
}) {
    const navigate = useNavigate();
    const { useGetRequest, useMutateRequest } = useRequest();

    const confirm = useConfirm();

    const auth = useAuth();
    const didAutofill = useRef(false);

    const farmOrgId = (auth.user?.organization as Organization)?.id;

    const setServiceProviderMutation = useMutateRequest({
        method: "put",
        errorMessage: "Could not save your CSP selection. Please try again.",
    });

    const [address, setAddress] = useState("");

    const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(
        null,
    );

    const [isGeocoding, setIsGeocoding] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [resolvedPlaceLabel, setResolvedPlaceLabel] = useState<string | null>(
        null,
    );

    useEffect(() => {
        if (address.trim()) return;

        if (didAutofill.current) return;

        const u = auth.user;
        if (!u) return;

        const composed = [u.address_1, u.address_2, u.city, u.state, u.zip]
            .filter(Boolean)
            .map((s) => String(s).trim())
            .filter(Boolean)
            .join(", ");

        if (!composed) return;

        setAddress(composed);
        didAutofill.current = true;
    }, [auth.user, address]);

    const nearbyCspQuery = useGetRequest<NearbyCsp[]>({
        url: "organizations/nearby_csps/",
        enabled: !!coords,
        params: coords ? { lat: coords.lat, lon: coords.lon } : undefined,
        queryOptions: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
        },
    });

    const { eliteCsps, defaultCsps } = useMemo(() => {
        const rows = nearbyCspQuery.data ?? [];

        const isElite = (c: NearbyCsp) =>
            c.organization.csp_preference === "preferred" &&
            c.distance_miles != null &&
            c.distance_miles <= 30;

        const elite = rows
            .filter(isElite)
            .slice()
            .sort((a, b) => (a.distance_miles ?? 0) - (b.distance_miles ?? 0));

        const defaults = rows
            .filter((c) => !isElite(c))
            .slice()
            .sort((a, b) => (a.distance_miles ?? 0) - (b.distance_miles ?? 0));

        return { eliteCsps: elite, defaultCsps: defaults };
    }, [nearbyCspQuery.data]);

    const sortedCsps = useMemo(
        () =>
            [...eliteCsps, ...defaultCsps].sort(
                (a, b) =>
                    (a.distance_miles ?? Infinity) -
                    (b.distance_miles ?? Infinity),
            ),
        [eliteCsps, defaultCsps],
    );

    const hasResults = !!coords && eliteCsps.length + defaultCsps.length > 0;

    const helperText = useMemo(() => {
        if (!coords) return null;

        if (resolvedPlaceLabel) {
            return `Using location: ${resolvedPlaceLabel}`;
        }

        return `Using coordinates: ${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}`;
    }, [coords, resolvedPlaceLabel]);

    useEffect(() => {
        onSearchResult?.({ coords, eliteCsps, defaultCsps });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coords, eliteCsps, defaultCsps]);

    function confirmSelectCsp(csp: NearbyCsp) {
        const locationText =
            [csp.city, csp.state].filter(Boolean).join(", ") || "—";

        const distanceText =
            typeof csp.distance_miles === "number"
                ? `${csp.distance_miles.toFixed(1)} mi`
                : null;

        confirm.showConfirm({
            title: "Select this CSP?",
            description: `${csp.organization.name} • ${locationText}${
                distanceText ? ` • ${distanceText}` : ""
            }`,
            confirmText: "Select",
            color: "primary",
            onConfirm: () => handleSelect(csp),
        });
    }

    async function handleSearch() {
        setError(null);

        const trimmed = address.trim();
        if (!trimmed) {
            setError("Enter an address to search.");
            return;
        }

        setIsGeocoding(true);
        setCoords(null);
        setResolvedPlaceLabel(null);

        try {
            const url = new URL(
                "https://api.mapbox.com/search/geocode/v6/forward",
            );

            url.searchParams.set("q", trimmed);
            url.searchParams.set("limit", "1");
            url.searchParams.set("autocomplete", "false");
            url.searchParams.set("types", "address,place,postcode");
            url.searchParams.set("access_token", getMapboxToken());

            const res = await fetch(url.toString(), { cache: "no-store" });
            if (!res.ok) {
                setError("Could not find that address. Please try again.");
                return;
            }
            const json = await res.json();
            const feature = json?.features?.[0];

            const lon = feature?.geometry?.coordinates?.[0];

            const lat = feature?.geometry?.coordinates?.[1];

            if (typeof lat !== "number" || typeof lon !== "number") {
                setError(
                    "Could not find a location for that address. Try a more specific address.",
                );
                return;
            }

            const label = String(
                feature?.properties?.full_address ?? "",
            ).trim();
            setResolvedPlaceLabel(label || null);

            setCoords({ lat, lon });
        } catch {
            setError("Could not find that address. Please try again.");
        } finally {
            setIsGeocoding(false);
        }
    }

    async function handleSelect(csp: NearbyCsp) {
        setError(null);

        if (!farmOrgId) {
            setError("Could not save your CSP selection. Please try again.");
            return;
        }

        try {
            await setServiceProviderMutation.mutateAsync({
                url: `organizations/${farmOrgId}/service_provider/`,
                data: { service_provider: csp.organization.id },
            });

            await auth.refreshUser?.();

            navigate({ to: "/" });
        } catch {
            setError("Could not save your CSP selection. Please try again.");
        }
    }

    const isNearbyLoading =
        !!coords && (nearbyCspQuery.isLoading || nearbyCspQuery.isFetching);
    const nearbyErrored = !!coords && nearbyCspQuery.isError;
    const showNoResults =
        !!coords && !isNearbyLoading && !nearbyErrored && !hasResults;

    return (
        <div className="flex flex-col gap-3 p-4 text-sm w-full">
            <div className="flex flex-col gap-1">
                <div className="text-lg font-semibold">Find a CSP near you</div>
                <div className="text-foreground-500">
                    Enter your address and we'll show nearby service providers.
                </div>
            </div>
            <div className="flex gap-2 items-end">
                <Input
                    label="Address"
                    value={address}
                    onValueChange={setAddress}
                    placeholder="Street Address, City, State, ZIP"
                    onKeyDown={(e) => {
                        if (e.key === "Enter") handleSearch();
                    }}
                />
                <Button
                    color="primary"
                    onPress={handleSearch}
                    isLoading={isGeocoding}
                >
                    Search
                </Button>
            </div>
            {helperText ? (
                <div className="text-xs text-foreground-500">{helperText}</div>
            ) : null}
            {error ? <div className="text-danger text-sm">{error}</div> : null}
            {isNearbyLoading ? (
                <div className="flex items-center gap-2 text-foreground-500">
                    <Spinner size="sm" />
                    Searching nearby CSPs…
                </div>
            ) : null}
            {nearbyErrored ? (
                <div className="text-danger text-sm">
                    Failed to load nearby CSPs. Please try again.
                </div>
            ) : null}
            {showNoResults ? (
                <div className="text-foreground-500">
                    No CSPs found within range.
                </div>
            ) : null}
            {hasResults ? (
                <div className="flex flex-col gap-4">
                    {eliteCsps.length ? (
                        <div className="flex flex-col gap-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                                Elite Providers
                            </div>

                            <div className="flex flex-col gap-2">
                                {eliteCsps.map((csp) => (
                                    <div
                                        key={csp.organization.id}
                                        ref={(el) => {
                                            if (el)
                                                cardRefs?.current.set(
                                                    csp.organization.id,
                                                    el,
                                                );
                                        }}
                                    >
                                        <CspCard
                                            csp={csp}
                                            isElite
                                            isSelected={
                                                selectedCspId ===
                                                csp.organization.id
                                            }
                                            onSelect={() =>
                                                confirmSelectCsp(csp)
                                            }
                                            onClick={() => onCspClick?.(csp)}
                                            isLoading={
                                                setServiceProviderMutation.isPending
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {defaultCsps.length ? (
                        <div className="flex flex-col gap-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
                                Providers
                            </div>

                            <div className="flex flex-col gap-2">
                                {defaultCsps.map((csp) => (
                                    <div
                                        key={csp.organization.id}
                                        ref={(el) => {
                                            if (el)
                                                cardRefs?.current.set(
                                                    csp.organization.id,
                                                    el,
                                                );
                                        }}
                                    >
                                        <CspCard
                                            csp={csp}
                                            isSelected={
                                                selectedCspId ===
                                                csp.organization.id
                                            }
                                            onSelect={() =>
                                                confirmSelectCsp(csp)
                                            }
                                            onClick={() => onCspClick?.(csp)}
                                            isLoading={
                                                setServiceProviderMutation.isPending
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
            {!!coords && !isNearbyLoading && !nearbyErrored ? (
                <SentinelContactCard csps={sortedCsps} />
            ) : null}
        </div>
    );
}
